import {
  type Currency,
  currencyUnitName,
  formatAmount,
  svgIconDataUri,
} from "@loot-os/api-contract";

import { cn, tv } from "../foundations/cn.js";
import { useCurrency } from "./currencyContext.js";

const amountStyles = tv({
  // Relative, so the spoken unit's sr-only span is held inside a row that scrolls it, not
  // positioned against the page, where it would widen the page past the screen.
  base: "relative inline-flex items-center gap-1 font-mono whitespace-nowrap tabular-nums",
  variants: {
    size: {
      meta: "text-mono-meta",
      md: "text-mono-price",
      lg: "text-mono-price-l",
    },
  },
  defaultVariants: { size: "md" },
});

const ICON_SIZE = { meta: "size-3", md: "size-3.5", lg: "size-5" } as const;

export interface AmountProps {
  /** Integer cents, the platform's unit for every amount. */
  cents: number;
  /** "$1.3k" under artwork; anything the shopper pays or is told a total for stays exact. */
  compact?: boolean;
  /** Drops ".00" from a whole amount ("Top pull $1,480"): values quoted beside art, not prices. */
  trimZeroCents?: boolean;
  size?: "meta" | "md" | "lg";
  /** Overrides the CurrencyProvider's currency. */
  currency?: Currency;
  className?: string;
}

/**
 * Money in the storefront's currency: mono, tabular, neutral, never truncated. A currency with
 * an icon shows it; one without a symbol is read out with its unit name ("1,250 caps").
 */
export function Amount({
  cents,
  compact = false,
  trimZeroCents = false,
  size,
  currency,
  className,
}: AmountProps) {
  const fromContext = useCurrency();
  const unit = currency ?? fromContext;
  const formatted = formatAmount(cents, unit, { compact });
  const text =
    trimZeroCents && cents % unit.multiplier === 0 ? formatted.replace(/\.0+$/, "") : formatted;
  const spoken = unit.symbol === "" ? `${text} ${currencyUnitName(cents, unit)}` : undefined;
  return (
    <span className={amountStyles({ size, className })}>
      {unit.iconSvg && (
        <img
          src={svgIconDataUri(unit.iconSvg)}
          alt=""
          className={cn("shrink-0", ICON_SIZE[size ?? "md"])}
        />
      )}
      {spoken ? (
        <>
          <span aria-hidden>{text}</span>
          <span className="sr-only">{spoken}</span>
        </>
      ) : (
        text
      )}
    </span>
  );
}
